import {
  paginateHandoverGroup,
  type HandoverGroup,
  type HandoverPage,
} from '@/lib/courierHandover';

export function waitForHandoverResources(root: HTMLElement, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let fontsReady = false,
      settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
      error ? reject(error) : resolve();
    };
    const abort = () => finish(new DOMException('Preparation cancelled.', 'AbortError'));
    const check = () => {
      if (signal.aborted) return abort();
      if (
        fontsReady &&
        Array.from(root.querySelectorAll('img')).every(
          (img) => img.complete && img.naturalWidth > 0,
        )
      )
        finish();
    };
    const poll = setInterval(check, 25);
    const timeout = setTimeout(
      () =>
        finish(new Error('The logo or document fonts could not be loaded. Retry before printing.')),
      6000,
    );
    signal.addEventListener('abort', abort, { once: true });
    void (document.fonts?.ready || Promise.resolve()).then(
      () => {
        fontsReady = true;
        check();
      },
      () => finish(new Error('Document fonts could not be loaded. Please retry.')),
    );
    check();
  });
}
export function measureHandoverPages(
  root: HTMLElement,
  groups: readonly HandoverGroup[],
): HandoverPage[] {
  const sheets = Array.from(root.querySelectorAll<HTMLElement>('.ho-page'));
  if (sheets.length !== groups.length)
    throw new Error('The worksheet layout changed. Prepare the preview again.');
  return groups.flatMap((group, index) => {
    const sheet = sheets[index]!,
      body = sheet.querySelector<HTMLElement>('.ho-body'),
      head = sheet.querySelector('thead');
    if (!body || !head) throw new Error('The A4 worksheet could not be measured. Try again.');
    const available = body.getBoundingClientRect().height - head.getBoundingClientRect().height - 3;
    const heights = Array.from(sheet.querySelectorAll('tbody tr')).map(
      (row) => row.getBoundingClientRect().height,
    );
    return paginateHandoverGroup(group, heights, available);
  });
}
export function assertHandoverFits(root: HTMLElement) {
  const sheets = Array.from(root.querySelectorAll<HTMLElement>('.ho-page'));
  if (!sheets.length) throw new Error('Prepare a worksheet before printing.');
  for (const sheet of sheets) {
    const body = sheet.querySelector<HTMLElement>('.ho-body'),
      table = sheet.querySelector('table');
    const overflow = Array.from(
      sheet.querySelectorAll<HTMLElement>('.ho-header,.ho-ack,.ho-body'),
    ).some((el) => el.scrollWidth > el.clientWidth + 1);
    if (
      !body ||
      !table ||
      table.getBoundingClientRect().height > body.getBoundingClientRect().height + 1 ||
      sheet.scrollHeight > sheet.clientHeight + 1 ||
      overflow
    )
      throw new Error(
        'These details do not fit the A4 worksheet at a readable size. Review long sender/courier details or use brand-only output.',
      );
  }
}
